<template>
    <div>
        <skeleton-loader v-if="loading" type="lib-rows" :count="perPage" />

        <div v-else-if="blocks.length === 0" class="lib-empty">
            <small>{{ $t('common.no_blocks_founded') }}</small>
        </div>

        <div v-else class="lib-list">
            <block-card v-for="block in blocks" :key="block.objectID" :block="block" with-menu
                @click:edit="showEditBlock(block)" @click:copy="confirmCopy(block)"
                @click:delete="showDeleteBlock(block.objectID)" no-duration-text @mousedown="startDrag"
                @click:share="showShareBlockDialog(block)" :canShare="canShare" />
        </div>

        <kimsa-pagination class="mt-4" @page="onPageChange" :value="page + 1" :pageCount="pageCount" center
            :total-visible="isMobile ? 4 : (totalVisiblePagination || 7)" />

        <block-card v-if="dragging" :block="draggingItem" with-menu no-duration-text class="floating-drag-item" />

        <delete-dragging-button :dragging="dragMoved" @mouseup="showDeleteBlock(draggingItem.objectID)" />

        <kimsa-confirm ref="showDelete" :title="$t('common.delete_block')" :content="$t('common.confirm_deleted_block')"
            :persistent="false" :loading="loadingDelete" @confirm="deleteBlock" @cancel="blockId = null"
            :close-on-confirm="false" :alert-mode="true" />
        
        <share-block-dialog ref="share" />

        <block-editor ref="blockEditor" :coach-id="coachId" @success="onBlockSaved" />

        <kimsa-confirm ref="confirmCopy" :title="$t('common.duplicate')" :content="$t('common.confirm_duplicate_block')"
            :persistent="false" :loading="loadingCopy" @confirm="onCopyBlock" @cancel="copyItem = null"
            :close-on-confirm="false" />

    </div>
</template>

<script>

import BlockCard from '@/components/common/routines/BlockCard'
import { BlockIndex } from "@/utils/Algolia";
import { mapActions } from "vuex";
import { db } from "@/utils/Firebase";
import KimsaConfirm from "@/components/common/KimsaConfirm";
import { debounce } from "@/utils/Common";
import SkeletonLoader from "@/components/common/SkeletonLoader";
import KimsaPagination from "@/components/common/KimsaPagination";
import { getPayloadBlock } from "@/utils/Exercises";
import DeleteDraggingButton from '../../common/DeleteDraggingButton.vue';
import draggingMixin from '../../../mixins/draggingMixin';
import ShareBlockDialog from './ShareBlockDialog.vue';
import BlockEditor from './BlockEditor.vue';
import algoliaInlineItemMixin from '../../../mixins/algoliaInlineItemMixin';

export default {
    mixins: [draggingMixin, algoliaInlineItemMixin],
    components: {
        KimsaPagination,
        SkeletonLoader,
        KimsaConfirm,
        BlockCard,
        DeleteDraggingButton,
        ShareBlockDialog,
        BlockEditor
    },
    props: {
        search: {
            type: String,
            default: '',
        },
        folder: {
            type: String,
            default: '',
        },
        totalVisiblePagination: {
            type: [Number, String],
            default: null,
        },
        coachId: {
            type: String,
            default: null,
        },
    },
    data() {
        return {
            loading: false,
            blocks: [],
            blockId: null,
            loadingDelete: false,
            coaches: [],
            page: 0,
            pageCount: 0,
            perPage: 6,
            copyItem: null,
            loadingCopy: false,
        }
    },
    computed: {
        companyId(vm = this) {
            return vm.account?.coachSubscription?.companyId || vm.currentUser.uid
        },
        canShare() {
            return this.isCompany || this.account?.accountType === 'coach_company'
        },
    },
    async mounted() {
        let vm = this
        vm.loading = true
        vm.isCompany ? await vm.loadCoaches() : await vm.loadBlocks()
    },
    methods: {
        ...mapActions(['fetchCompanyCoaches']),
        async loadCoaches() {
            const vm = this
            const coaches = await vm.fetchCompanyCoaches(vm.companyId)
            vm.coaches = [vm.coachId, ...coaches.map(c => c.objectID)]
        },
        debouncedLoadBlocks: debounce(
            function () {
                this.loadBlocks()
            }, 1000),
        async loadBlocks() {
            const vm = this
            await vm.withLoading(async () => {
                vm.blocks = []
                let coaches = vm.coaches.length === 0 ? vm.coachId : vm.coaches.join(' OR coachId:')
                let filter = `coachId: ${coaches}`
                if (vm.folder) filter += ` AND folder: ${vm.folder}`
                let response = await BlockIndex.search(vm.search, {
                    filters: filter,
                    hitsPerPage: vm.perPage,
                    page: vm.page,
                })
                vm.pageCount = response.nbPages || 0
                vm.blocks = vm.filterDeletedInline(response.hits)
            })
        },
        refresh() {
            const vm = this;
            vm.loadBlocks()
        },
        onPageChange(p) {
            const vm = this
            const newPage = Number(p) - 1
            if (newPage === vm.page) return
            vm.page = newPage
            vm.loadBlocks()
        },
        showEditBlock(block) {
            const vm = this;
            vm.$refs.blockEditor.showEdit(block)
        },
        onBlockSaved(block) {
            const vm = this
            if (!vm.addItemInline(vm.blocks, block, 'folder', vm.folder)) vm.loadBlocks()
        },
        confirmCopy(block) {
            const vm = this
            vm.resetDrag()
            vm.copyItem = block
            vm.$refs.confirmCopy.show()
        },
        async onCopyBlock() {
            let vm = this
            const block = vm.copyItem
            await vm.withLoading(async () => {
                let payload = getPayloadBlock(
                    `${block.name} ${vm.$t('common.copy_template')}`,
                    vm.coachId,
                    block.block,
                    { id: block.folder, name: block.folderName },
                    block?.tags || [],
                    { img: block.img, imgSavePath: block.imgSavePath }
                )

                let newblock = await db.collection('blocks').add(payload)
                vm.toast(vm.$t('common.block_copied'))
                vm.$refs.confirmCopy?.hide()

                vm.$refs.blockEditor.showEdit({ objectID: newblock.id })
            }, 'loadingCopy')
        },
        async showDeleteBlock(id) {
            const vm = this
            vm.resetDrag()
            vm.blockId = id
            vm.$refs.showDelete.show()
        },
        async deleteBlock() {
            const vm = this
            await vm.withLoading(async () => {
                await db.collection('blocks').doc(vm.blockId).delete()
                vm.blocks = vm.markDeletedInline(vm.blocks, vm.blockId)
                vm.toast(vm.$t('common.block_deleted'))
                vm.blockId = null
                vm.$refs.showDelete?.hide()
            }, 'loadingDelete')
        },
        handleDoubleClickDragging(item) {
            const vm = this;
            vm.confirmCopy(item)
        },
        handleClickDragging(item) {
            const vm = this;
            vm.showEditBlock(item)
        },
        showShareBlockDialog(block) {
            const vm = this;
            vm.$refs.share.show(block)
        }
    },
    watch: {
        coaches() {
            this.loadBlocks()
        },
        search(val) {
            this.page = 0
            if (!val) return this.loadBlocks()
            return this.debouncedLoadBlocks()
        },
        folder() {
            this.page = 0
            this.loadBlocks()
        },
        loading(val) {
            this.$emit('loading', val)
        },
    }
}
</script>

<style scoped>
.lib-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.lib-empty {
    text-align: center;
    padding: 32px 0;
    color: var(--v-txtMuted-base);
}

.floating-drag-item {
    position: fixed;
    z-index: 9999;
    opacity: var(--drag-opacity);
    width: 250px;
    left: 0;
    top: 0;
    transform: translate(var(--drag-x), var(--drag-y));
    pointer-events: none;
}
</style>
